import { Component, inject } from '@angular/core';
import { PrimaryInputComponent } from "../../../components/input/primary-input/primary-input.component";
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ImagesService } from '../../../services/general/images.service';
import { AuthService } from '../../../services/auth/auth.service';
import { ToastService } from '../../../services/general/toast.service';

@Component({
  selector: 'app-forgot-password',
  imports: [
    PrimaryInputComponent,
    CommonModule,
    ReactiveFormsModule,
  ],
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.scss'
})
export class ForgotPasswordComponent {
  _router = inject(Router);
  _fb = inject(FormBuilder);
  _images = inject(ImagesService);
  _authService = inject(AuthService);
  _toast = inject(ToastService);

  currentStep: number = 1; // 1 = email step, 2 = reset password step
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;
  isSendingCode: boolean = false;
  isResettingPassword: boolean = false;
  currentYear: number = new Date().getFullYear();
  resetKey: string = ''; // Store the key from forgot-password response

  // Step 1: Email form
  emailForm: FormGroup = this._fb.group({
    email: ['', [Validators.required, Validators.email]],
  });

  // Step 2: Reset password form
  resetPasswordForm: FormGroup = this._fb.group({
    code: ['', [Validators.required, Validators.minLength(6)]],
    newPassword: ['', [Validators.required, Validators.minLength(6)]],
    confirmPassword: ['', [Validators.required]],
  }, {
    validators: this.passwordMatchValidator
  });

  passwordMatchValidator(formGroup: FormGroup) {
    const newPassword = formGroup.get('newPassword')?.value;
    const confirmPassword = formGroup.get('confirmPassword')?.value;

    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      formGroup.get('confirmPassword')?.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    } else {
      formGroup.get('confirmPassword')?.setErrors(null);
      return null;
    }
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  // Step 1: Submit email
  submitEmail() {
    if (this.emailForm.valid) {
      this.isSendingCode = true;
      const email = this.emailForm.get('email')?.value;

      this._authService.sendForgotPasswordCode(email).subscribe({
        next: (response) => {
          this.isSendingCode = false;
          // Store the key from the response
          this.resetKey = response.key;
          this._toast.success(response.message || 'Verification code sent successfully');
          // Move to step 2
          this.currentStep = 2;
        },
        error: (error) => {
          this.isSendingCode = false;
          console.error('Error sending verification code:', error);
          this._toast.error('Failed to send verification code. Please try again.');
        }
      });
    } else {
      this.emailForm.markAllAsTouched();
    }
  }

  // Step 2: Reset password
  resetPassword() {
    if (this.resetPasswordForm.valid && this.resetKey) {
      this.isResettingPassword = true;
      const code = this.resetPasswordForm.get('code')?.value;
      const password = this.resetPasswordForm.get('newPassword')?.value;
      const passwordConfirmation = this.resetPasswordForm.get('confirmPassword')?.value;

      this._authService.resetPassword(this.resetKey, code, password, passwordConfirmation).subscribe({
        next: (response) => {
          this.isResettingPassword = false;
          this._toast.success('Password reset successfully');
          // Navigate to login after successful reset
          this._router.navigate(['auth/login']);
        },
        error: (error) => {
          this.isResettingPassword = false;
          console.error('Error resetting password:', error);

          // Check if it's a 400 error (invalid code)
          if (error.status === 400) {
            this._toast.error('Invalid verification code. Please check and try again.');
            // Optionally clear the code field
            this.resetPasswordForm.get('code')?.setValue('');
            this.resetPasswordForm.get('code')?.markAsUntouched();
          } else {
            this._toast.error('Failed to reset password. Please try again.');
          }
        }
      });
    } else {
      this.resetPasswordForm.markAllAsTouched();
    }
  }

  // Go back to step 1
  goBackToEmailStep() {
    this.currentStep = 1;
    // Reset the key when going back
    this.resetKey = '';
    // Reset the form
    this.resetPasswordForm.reset();
  }

  // Navigate to login
  goToLogin() {
    this._router.navigate(['auth/login']);
  }
}

